/*

  Userlist.ts

  User list display component

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjalmur Thorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { IView, UserListItem } from "./types";
import { ts, t, mt } from "./i18n";
import { ComponentFunc, VnodeChildren, m } from "./mithril";
import { glyph, glyphGrayed, nbsp } from "./util";
import { ChallengeButton, UserInfoButton } from "./components";
import { EloPage } from "./elopage";

interface IAttributes {
  view: IView;
  id: string;
}

export const UserList: ComponentFunc<IAttributes> = () => {
  // Shows a list of users, obtained from view.model.userList

  function itemize(view: IView, item: UserListItem, i: number): VnodeChildren {

    // Generate a list item about a user

    const isRobot = item.userid.indexOf("robot-") === 0;
    let fullname: VnodeChildren = [];

    // Online and accepting challenges
    if (item.ready && !isRobot) {
      fullname.push(m("span.ready-btn", { title: ts("Álínis og tekur við áskorunum") }));
      fullname.push(nbsp());
    }
    // Willing to accept challenges for timed games
    if (item.ready_timed) {
      fullname.push(m("span.timed-btn", { title: ts("Til í viðureign með klukku") }));
      fullname.push(nbsp());
    }
    // Fair play commitment
    if (item.fairplay) {
      fullname.push(m("span.fairplay-btn", { title: ts("Skraflar án hjálpartækja") }));
      fullname.push(nbsp());
    }
    fullname.push(item.fullname);

    function fav(): VnodeChildren {
      if (isRobot)
        return m("span.list-fav", { style: { cursor: "default" } }, glyph("star-empty"));
      return m("span.list-fav",
        {
          title: ts("Uppáhald"),
          onclick: (ev: Event) => {
            view.actions.toggleFavorite(item.userid, item.fav);
            item.fav = !item.fav;
            ev.preventDefault();
          }
        },
        glyph(item.fav ? "star" : "star-empty")
      );
    }

    function userLink(): VnodeChildren {
      if (isRobot)
        return m("a",
          {
            href: "",
            onclick: (ev: Event) => {
              // Start a new game against the robot
              view.actions.startRobotGame(item.userid);
              ev.preventDefault();
            }
          },
          [
            m("span.list-nick", [glyph("cog"), nbsp(), item.nick]),
            m("span.list-fullname-robot", fullname)
          ]
        );
      else
        return [
          m("span.list-nick", item.nick),
          m("span.list-fullname", fullname),
          m("span.list-human-elo", item.human_elo)
        ];
    }

    return m(".listitem" + (i % 2 === 0 ? ".oddlist" : ".evenlist"),
      [
        m(ChallengeButton, { view, item }),
        fav(),
        userLink(),
        m(UserInfoButton, { view, item }),
      ]
    );
  }

  return {
    view: (vnode) => {
      const { view, id } = vnode.attrs;
      const { model } = view;

      // The type of list to show; by default it's 'robots'
      const query = model.userListCriteria?.query ?? "";
      const spec = model.userListCriteria?.spec ?? "";
      const loadingElo = model.eloRatingSpec === undefined;
      const listType = query || "robots";

      if (listType === "elo" || loadingElo)
        // Show Elo list
        return [
          m(EloPage, {
            id: "elolist",
            view: view,
            spec: model.eloRatingSpec ?? null,
            key: "elopage",
          })
        ];

      // Show normal user list
      let list: UserListItem[] = [];
      if (model.userList === undefined) {
        // We are loading a fresh user list
        /* pass */
      }
      else if (model.userList === null || query !== listType) {
        view.actions.loadUsersByType(listType, true);
      } else {
        list = model.userList;
      }

      const nothingFound = list.length === 0 && listType === "search" && spec !== "";
      const robotList = listType === "robots";

      return [
        m(".listitem.listheader",
          [
            m("span.list-ch", glyphGrayed("hand-right", { title: ts('Skora á') })),
            m("span.list-fav", glyph("star-empty", { title: ts('Uppáhald') })),
            mt("span.list-nick", "Einkenni"),
            mt("span.list-fullname", "Nafn og merki"),
            robotList ? "" : mt("span.list-human-elo[id='usr-list-elo']", "Elo"),
            robotList ? "" : mt("span.list-info-hdr[id='usr-list-info']", "Ferill"),
          ]
        ),
        m("div", { id }, list.map((item, i) => itemize(view, item, i))),
        // Show indicator if search didn't find any users matching the criteria
        nothingFound ?
          m("div",
            { id: "user-no-match", style: { display: "block" } },
            [
              glyph("search"),
              " ",
              m("span", { id: "search-prefix" }, spec),
              t(" finnst ekki")
            ]
          )
          : undefined
      ];
    }
  };
};
